import { AccountInfo } from '@azure/msal-browser';

import { apiScopes } from './msal';
import { getMsalInstance } from './msalInstance';

const getAccount = (): AccountInfo | null => {
  const instance = getMsalInstance();
  return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
};

/**
 * Acquire a currently valid API access token from MSAL, or null when no
 * account is signed in yet (callers must then leave their request/connection
 * config untouched). MSAL serves its cache while the token is valid, so
 * calling this per request costs no network round-trip.
 */
export const acquireApiToken = async (): Promise<string | null> => {
  const account = getAccount();
  if (!account) {
    return null;
  }
  const result = await getMsalInstance().acquireTokenSilent({
    scopes: apiScopes,
    account,
  });
  return result.accessToken;
};

let refreshInFlight: Promise<string | null> | null = null;

const runForceRefresh = async (): Promise<string | null> => {
  const account = getAccount();
  if (!account) {
    return null;
  }
  const result = await getMsalInstance().acquireTokenSilent({
    scopes: apiScopes,
    account,
    forceRefresh: true,
  });
  return result.accessToken;
};

/**
 * Force a token refresh, bypassing the MSAL cache. Single-flight: concurrent
 * callers (e.g. parallel 401s after an idle tab) share one refresh instead of
 * each triggering their own round-trip to Azure AD.
 *
 * Cleanup is chained via .finally() rather than done inside the async body:
 * a body that completes synchronously (no signed-in account) would run its
 * own finally BEFORE the promise is assigned to refreshInFlight, leaving a
 * permanently settled promise behind.
 */
export const forceRefreshApiToken = (): Promise<string | null> => {
  if (!refreshInFlight) {
    const pending = runForceRefresh().finally(() => {
      if (refreshInFlight === pending) {
        refreshInFlight = null;
      }
    });
    refreshInFlight = pending;
  }
  return refreshInFlight;
};
